import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  IndustryCreateSchema,
  IndustryQueryAllSchema,
  IndustryQuerySchema,
  IndustryUpdateSchema,
} from './schemas';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INDUSTRIES'] });
};

export const useGetIndustriesMutation: UseControlledMutation<IndustryQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['INDUSTRIES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<IndustryQueryAllSchema>({
            endpoint: '/api/v1/industries',
            params: { search, per_page, include: 'parent' },
          });

          // https://digiprojects.atlassian.net/browse/SW-895
          // removed filtering out parentless industries
          return orderBy(res?.data ?? [], ['name']); // .filter((industry) => !!industry.parent);
        },
      });
    },
  });

export function useGetIndustries(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['INDUSTRIES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<IndustryQueryAllSchema>({
        endpoint: '/api/v1/industries',
        params: `${apiParamStr}&include=parent,clientsCount`,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, IndustryCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/industries/',
        payload,
      }),
  });

export function useGetIndustry(id: string) {
  return useSuspenseQuery({
    queryKey: ['INDUSTRY', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: IndustryQuerySchema }>({
        endpoint: `/api/v1/industries/${id}`,
        params: { include: 'parent' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: IndustryQuerySchema }, ApiError, Partial<IndustryUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/industries/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INDUSTRY_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/industries/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
